import { useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchApi from '@/utils/fetchApi'
import { BULK_DELETE_BATCH_SIZE } from '@/constants/documents.ts'

interface BulkDeleteResult {
  ok: boolean
  failedIds: string[]
  errors?: string[]
}

export function useDeleteVariablesBulkMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkDeleteResult> => {
      if (ids.length === 0) {
        return { ok: true, failedIds: [] }
      }

      const chunks: string[][] = []
      for (let i = 0; i < ids.length; i += BULK_DELETE_BATCH_SIZE) {
        chunks.push(ids.slice(i, i + BULK_DELETE_BATCH_SIZE))
      }

      const failedIds: string[] = []
      const errors: string[] = []

      for (const chunk of chunks) {
        try {
          const res = await fetchApi<{
            deletedIds?: string[]
            failed?: { id: string; error?: string }[]
          }>({
            endpoint: '/api/variables/bulk-delete',
            method: 'POST',
            body: { ids: chunk },
          })

          const failed = res.failed ?? []
          failed.forEach((item) => {
            failedIds.push(item.id)
            if (item.error) {
              errors.push(item.error)
            }
          })
        } catch (err) {
          console.error('Error deleting variables in bulk chunk:', err)
          failedIds.push(...chunk)
        }
      }

      return failedIds.length > 0
        ? { ok: false, failedIds, ...(errors.length > 0 ? { errors } : {}) }
        : { ok: true, failedIds: [] }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['variables', 'list'] })
      void queryClient.invalidateQueries({ queryKey: ['variables', 'all'] })
    },
  })
}
